(() => {
  'use strict';

  if (window.TodayMerchLineItemProperties) return;

  const CONTRACT_VERSION = '1';
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);
  const isObject = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));
  const text = (value) => String(value == null ? '' : value).trim();
  const uniqueStrings = (values) => Array.from(new Set((Array.isArray(values) ? values : [])
    .map(text)
    .filter(Boolean)));

  const parseJson = (value, fallback = null) => {
    if (isObject(value) || Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(String(value || ''));
      return parsed == null ? fallback : parsed;
    } catch (error) {
      return fallback;
    }
  };

  const parseIdArray = (value, { allowLegacyList = false } = {}) => {
    const parsed = parseJson(value, null);
    if (Array.isArray(parsed)) return uniqueStrings(parsed);
    if (!allowLegacyList) return [];
    return uniqueStrings(String(value || '').split(','));
  };

  const parseLegacyFileNames = (value) => String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

  const firstValue = (properties, keys) => {
    for (const key of keys) {
      if (own(properties, key) && text(properties[key])) return properties[key];
    }
    return '';
  };

  const fileNameFromValue = (value) => {
    const raw = text(value);
    if (!raw) return '';
    try {
      const url = new URL(raw, 'https://todaymerch.invalid');
      return decodeURIComponent(url.pathname.split('/').pop() || '');
    } catch (error) {
      return raw.split(/[\\/]/).pop().split('?')[0].split('#')[0];
    }
  };

  const collectCanonicalAssets = (properties, prefix) => Object.keys(properties || {})
    .map((key) => {
      const match = key.match(new RegExp(`^${prefix}_(\\d+)$`));
      if (!match || !text(properties[key])) return null;
      const index = Number(match[1]);
      return {
        index,
        key,
        value: properties[key],
        fileName: fileNameFromValue(properties[key]),
        zoneId: text(properties[`${key}_zone_id`]),
        viewId: text(properties[`${key}_view_id`]),
        source: 'canonical',
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.index - right.index);

  const collectLegacyAssets = (properties, kind) => Object.keys(properties || {})
    .map((key) => {
      let match;
      if (kind === 'design') {
        match = key.match(/^(?:Design original|Original design)\s+(\d+)\s*-\s*(.+)$/iu) ||
          key.match(/^Logo\s*-\s*(.+)$/iu) ||
          key.match(/^Studio artwork\s+(\d+)$/iu);
      } else {
        match = key.match(/^(?:Mockup|Mock-up)\s*-\s*(.+)$/iu);
      }
      if (!match || !text(properties[key])) return null;
      const firstIsIndex = /^\d+$/u.test(String(match[1] || ''));
      return {
        index: firstIsIndex ? Number(match[1]) : 0,
        key,
        value: properties[key],
        fileName: fileNameFromValue(properties[key]),
        zoneId: '',
        viewId: '',
        legacyZoneLabel: text(match[firstIsIndex ? 2 : 1]),
        source: 'legacy',
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.index - right.index);

  const parseMockupStatus = (value) => {
    const parsed = parseJson(value, {});
    const version = Number(parsed?.version || 1);
    const zones = Array.isArray(parsed?.zones) ? parsed.zones.slice(0, 24).map((zone) => ({
      key: text(zone?.key),
      status: text(zone?.status),
      reason: text(zone?.reason),
      fileName: text(zone?.fileName || zone?.file_name),
      size: Number(zone?.size || 0),
      legacyLabel: version < 2 ? text(zone?.label) : '',
    })) : [];
    return { version, zones };
  };

  const knownPublicProperty = (key) => (
    /^(?:Personnalisation|Personalization|Logo|Estimation marquage|Branding estimate|Tailles|Sizes|Studio Today Merch|Today Merch Studio|Studio TodayMerch|TodayMerch Studio|Studio - (?:Vue|View|Zone|Fichier|File|Placement))$/u.test(key) ||
    /^(?:(?:Design original|Original design)\s+\d+|(?:Mockup|Mock-up|Logo)\s*-|Studio artwork\s+\d+)/iu.test(key)
  );

  const knownPrivateProperty = (key) => (
    /^_tm_(?:line_contract_version|branding_|mockup_status|studio_|size_|apparel)/u.test(key) ||
    /^_(?:marking_|studio_)/u.test(key) ||
    key === '_moq' || /^_(?:pricing|applied)_/u.test(key)
  );

  const isSafeMutationKey = (key) => {
    const propertyKey = text(key);
    if (!propertyKey) return false;
    return propertyKey.startsWith('_') || !knownPublicProperty(propertyKey);
  };

  const isPreservedLegacyFileUrl = (key, value) => {
    const propertyKey = text(key);
    const propertyValue = text(value);
    if (!/^(?:Studio artwork\s+\d+|(?:Design original|Original design)\s+\d+\s*-\s*.+|Logo\s*-\s*.+)$/iu.test(propertyKey)) return false;
    return /^(?:https?:)?\/\//iu.test(propertyValue) || /^\/uploads\//u.test(propertyValue);
  };

  const resolve = (input) => {
    const properties = isObject(input) ? input : {};
    const canonicalDesigns = collectCanonicalAssets(properties, '_tm_branding_design');
    const canonicalMockups = collectCanonicalAssets(properties, '_tm_branding_mockup');
    const canonicalStudioArtworks = collectCanonicalAssets(properties, '_tm_studio_artwork');
    const canonicalStudioPreviews = collectCanonicalAssets(properties, '_tm_studio_preview');
    const canonicalFileNames = parseIdArray(properties._tm_studio_artwork_file_names);
    const oldFileNameValue = firstValue(properties, ['_studio_artwork_file_name', 'Studio - Fichier', 'Studio - File']);
    const canonicalTransform = parseJson(properties._tm_studio_transform, null);
    const oldTransform = parseJson(properties._studio_artwork_transform, null);
    const sizeBreakdown = parseJson(properties._tm_size_breakdown, {});
    const authored = Object.entries(properties).reduce((entries, [key, value]) => {
      if (!text(value) || key.startsWith('_') || knownPublicProperty(key) || knownPrivateProperty(key)) return entries;
      entries.push({ key, value });
      return entries;
    }, []);

    return {
      version: text(properties._tm_line_contract_version),
      branding: {
        profileId: text(firstValue(properties, ['_tm_branding_profile_id', '_marking_profile'])),
        zoneIds: own(properties, '_tm_branding_zone_ids')
          ? parseIdArray(properties._tm_branding_zone_ids)
          : parseIdArray(properties._marking_zone_keys, { allowLegacyList: true }),
        logoZoneIds: own(properties, '_tm_branding_logo_zone_ids')
          ? parseIdArray(properties._tm_branding_logo_zone_ids)
          : parseIdArray(properties._marking_logo_zone_keys, { allowLegacyList: true }),
        legacyZoneLabels: text(firstValue(properties, ['Personnalisation', 'Personalization']))
          .split(',').map((entry) => entry.trim()).filter(Boolean),
        estimateCode: text(properties._tm_branding_estimate_code),
        legacyEstimate: firstValue(properties, ['Estimation marquage', 'Branding estimate']),
        designs: canonicalDesigns.length ? canonicalDesigns : collectLegacyAssets(properties, 'design'),
        mockups: canonicalMockups.length ? canonicalMockups : collectLegacyAssets(properties, 'mockup'),
        status: parseMockupStatus(properties._tm_mockup_status),
      },
      studio: {
        enabled: text(firstValue(properties, ['_tm_studio_enabled', 'Studio Today Merch', 'Today Merch Studio', 'Studio TodayMerch', 'TodayMerch Studio'])),
        viewId: text(firstValue(properties, ['_tm_studio_view_id', '_studio_view'])),
        zoneId: text(firstValue(properties, ['_tm_studio_zone_id', '_studio_zone_key'])),
        legacyViewLabel: text(firstValue(properties, ['Studio - Vue', 'Studio - View'])),
        legacyZoneLabel: text(properties['Studio - Zone']),
        bounds: firstValue(properties, ['_tm_studio_bounds', '_studio_zone_bounds']),
        transform: canonicalTransform || oldTransform || {},
        fileNames: canonicalFileNames.length ? canonicalFileNames : parseLegacyFileNames(oldFileNameValue),
        artworks: canonicalStudioArtworks.length ? canonicalStudioArtworks : Object.keys(properties)
          .filter((key) => /^Studio artwork\s+\d+$/iu.test(key) && text(properties[key]))
          .map((key) => ({ key, value: properties[key], fileName: fileNameFromValue(properties[key]), source: 'legacy' })),
        previews: canonicalStudioPreviews.length ? canonicalStudioPreviews : collectCanonicalAssets(properties, '_studio_cart_preview'),
        manifest: parseJson(properties._tm_studio_preview_manifest, null) || parseJson(properties._studio_cart_preview_manifest, null),
      },
      sizes: {
        summary: text(firstValue(properties, ['_tm_size_summary', 'Tailles', 'Sizes'])),
        breakdown: isObject(sizeBreakdown) ? sizeBreakdown : {},
      },
      authored,
    };
  };

  const addIndexedAssets = (output, prefix, assets) => {
    (Array.isArray(assets) ? assets : []).forEach((asset, index) => {
      if (!asset || asset.value == null || asset.value === '') return;
      const propertyKey = `${prefix}_${index + 1}`;
      output[propertyKey] = asset.value;
      const zoneId = text(asset.zoneId);
      const viewId = text(asset.viewId);
      if (zoneId) output[`${propertyKey}_zone_id`] = zoneId;
      if (viewId) output[`${propertyKey}_view_id`] = viewId;
    });
  };

  const buildBrandingProperties = (input = {}) => {
    const output = { _tm_line_contract_version: CONTRACT_VERSION };
    const profileId = text(input.profileId);
    const zoneIds = uniqueStrings(input.zoneIds);
    const logoZoneIds = uniqueStrings(input.logoZoneIds);
    const estimateCode = ['custom', 'multiple'].includes(text(input.estimateCode)) ? text(input.estimateCode) : '';
    if (profileId) output._tm_branding_profile_id = profileId;
    if (zoneIds.length) output._tm_branding_zone_ids = JSON.stringify(zoneIds);
    if (logoZoneIds.length) output._tm_branding_logo_zone_ids = JSON.stringify(logoZoneIds);
    if (estimateCode) output._tm_branding_estimate_code = estimateCode;
    addIndexedAssets(output, '_tm_branding_design', input.designs);
    addIndexedAssets(output, '_tm_branding_mockup', input.mockups);

    if (Array.isArray(input.statusZones) && input.statusZones.length) {
      output._tm_mockup_status = JSON.stringify({
        version: 2,
        zones: input.statusZones.slice(0, 24).map((zone) => ({
          key: text(zone?.key),
          status: text(zone?.status),
          reason: text(zone?.reason),
          fileName: text(zone?.fileName || zone?.file_name),
          size: Number(zone?.size || 0),
        })),
      });
    }
    return output;
  };

  const stripLocalizedTransformFields = (value) => {
    if (Array.isArray(value)) return value.map(stripLocalizedTransformFields);
    if (!isObject(value)) return value;
    return Object.entries(value).reduce((output, [key, entry]) => {
      const normalizedKey = key.toLowerCase();
      if (normalizedKey === 'label' || normalizedKey === 'alt' || normalizedKey === 'viewlabel' ||
        normalizedKey === 'zonelabel' || normalizedKey === 'placementlabel' || normalizedKey === 'displaytext' ||
        normalizedKey === 'view' || normalizedKey === 'zone' || normalizedKey === 'placement') return output;
      output[key] = stripLocalizedTransformFields(entry);
      return output;
    }, {});
  };

  const buildStudioProperties = (input = {}) => {
    const output = { _tm_line_contract_version: CONTRACT_VERSION };
    if (!input.enabled) return output;
    output._tm_studio_enabled = '1';
    const viewId = text(input.viewId);
    const zoneId = text(input.zoneId);
    if (viewId) output._tm_studio_view_id = viewId;
    if (zoneId) output._tm_studio_zone_id = zoneId;
    if (input.bounds) output._tm_studio_bounds = typeof input.bounds === 'string'
      ? input.bounds
      : JSON.stringify(input.bounds);
    if (isObject(input.transform)) output._tm_studio_transform = JSON.stringify(stripLocalizedTransformFields(input.transform));
    const fileNames = uniqueStrings((Array.isArray(input.artworks) ? input.artworks : []).map((asset) => asset?.fileName));
    if (fileNames.length) output._tm_studio_artwork_file_names = JSON.stringify(fileNames);
    addIndexedAssets(output, '_tm_studio_artwork', input.artworks);
    addIndexedAssets(output, '_tm_studio_preview', input.previews);
    if (isObject(input.manifest) && Number(input.manifest.version) === 2) {
      output._tm_studio_preview_manifest = JSON.stringify(input.manifest);
    }
    return output;
  };

  const buildSizeProperties = (breakdown, summary) => {
    const output = {
      _tm_line_contract_version: CONTRACT_VERSION,
      _tm_apparel: 'true',
    };
    if (isObject(breakdown) && Object.keys(breakdown).length) {
      output._tm_size_breakdown = JSON.stringify(breakdown);
      output._tm_size_summary = text(summary);
    }
    return output;
  };

  const deleteKeys = (object, predicate) => {
    Object.keys(object).forEach((key) => {
      if (predicate(key)) delete object[key];
    });
  };

  const materializeCanonical = (original, canonical) => {
    const output = { ...(isObject(original) ? original : {}), ...(isObject(canonical) ? canonical : {}) };
    const hasCanonicalValue = (key) => own(canonical, key) && canonical[key] != null && (typeof canonical[key] !== 'string' || canonical[key].trim());
    const canonicalKeys = Object.keys(isObject(canonical) ? canonical : {}).filter(hasCanonicalValue);
    const hasBrandingDesign = canonicalKeys.some((key) => /^_tm_branding_design_\d+$/u.test(key));
    const hasBrandingMockup = canonicalKeys.some((key) => /^_tm_branding_mockup_\d+$/u.test(key));
    const hasStudioArtwork = canonicalKeys.some((key) => /^_tm_studio_artwork_\d+$/u.test(key));
    const hasStudioPreview = canonicalKeys.some((key) => /^_tm_studio_preview_\d+$/u.test(key));
    const hasSizes = canonicalKeys.some((key) => /^_tm_size_(?:summary|breakdown)$/u.test(key));

    if (hasCanonicalValue('_tm_branding_profile_id')) delete output._marking_profile;
    if (hasCanonicalValue('_tm_branding_zone_ids')) {
      delete output.Personnalisation;
      delete output.Personalization;
      delete output._marking_zone_keys;
    }
    if (hasCanonicalValue('_tm_branding_logo_zone_ids')) delete output._marking_logo_zone_keys;
    if (hasCanonicalValue('_tm_branding_estimate_code')) {
      delete output['Estimation marquage'];
      delete output['Branding estimate'];
    }
    if (hasBrandingDesign) deleteKeys(output, (key) => /^(?:(?:Design original|Original design)\s+\d+|Logo\s*-)/iu.test(key));
    if (hasBrandingMockup) deleteKeys(output, (key) => /^(?:Mockup|Mock-up)\s*-/iu.test(key));
    if (hasCanonicalValue('_tm_studio_enabled')) {
      delete output['Studio Today Merch'];
      delete output['Today Merch Studio'];
      delete output['Studio TodayMerch'];
      delete output['TodayMerch Studio'];
    }
    if (hasCanonicalValue('_tm_studio_view_id')) {
      delete output['Studio - Vue'];
      delete output['Studio - View'];
      delete output._studio_view;
    }
    if (hasCanonicalValue('_tm_studio_zone_id')) {
      delete output['Studio - Zone'];
      delete output._studio_zone_key;
    }
    if (hasCanonicalValue('_tm_studio_bounds')) delete output._studio_zone_bounds;
    if (hasCanonicalValue('_tm_studio_transform')) delete output._studio_artwork_transform;
    if (hasCanonicalValue('_tm_studio_artwork_file_names')) {
      delete output['Studio - Fichier'];
      delete output['Studio - File'];
      delete output._studio_artwork_file_name;
    }
    if (hasCanonicalValue('_tm_studio_preview_manifest')) delete output._studio_cart_preview_manifest;
    if (hasCanonicalValue('_tm_studio_view_id') && hasCanonicalValue('_tm_studio_zone_id')) {
      delete output['Studio - Placement'];
    }
    if (hasStudioArtwork) deleteKeys(output, (key) => /^Studio artwork\s+\d+$/iu.test(key));
    if (hasStudioPreview) deleteKeys(output, (key) => /^_studio_cart_preview_\d+$/u.test(key));
    if (hasSizes) deleteKeys(output, (key) => key === 'Tailles' || key === 'Sizes');

    deleteKeys(output, (key) => output[key] == null || (typeof output[key] === 'string' && !output[key].trim()));
    return output;
  };

  window.TodayMerchLineItemProperties = Object.freeze({
    CONTRACT_VERSION,
    buildBrandingProperties,
    buildSizeProperties,
    buildStudioProperties,
    materializeCanonical,
    parseMockupStatus,
    resolve,
    isPreservedLegacyFileUrl,
    isSafeMutationKey,
  });
})();
